import { el } from './dom';

export type Snap = 'closed' | 'half' | 'full';

const FRACTION: Record<Snap, number> = { closed: 1, half: 0.55, full: 0.06 };

/**
 * Bottom sheet with drag and three snap points. Everything reachable with one
 * thumb: the handle and header are the drag area, the body scrolls once open.
 */
export class Sheet {
  readonly root: HTMLElement;
  readonly body: HTMLElement;
  private readonly header: HTMLElement;
  private snap: Snap = 'closed';
  private dragFrom: number | null = null;
  private dragStartY = 0;
  private height = 0;

  constructor(title: string, opts: { onSnap?: (s: Snap) => void } = {}) {
    this.body = el('div', { class: 'sheet-body' });
    this.header = el('div', { class: 'sheet-header' }, [
      el('div', { class: 'sheet-grip' }),
      el('h2', { class: 'sheet-title', text: title }),
      el('button', {
        class: 'sheet-close',
        attrs: { type: 'button', 'aria-label': 'Zavřít' },
        text: '✕',
        on: { click: () => this.setSnap('closed') },
      }),
    ]);
    this.root = el('section', { class: 'sheet', attrs: { 'aria-hidden': 'true' } }, [this.header, this.body]);

    this.header.addEventListener('pointerdown', this.onDown);
    this.onSnapChange = opts.onSnap;
    this.apply(false);
  }

  private onSnapChange?: (s: Snap) => void;

  get currentSnap(): Snap {
    return this.snap;
  }

  setTitle(text: string): void {
    this.header.querySelector('.sheet-title')!.textContent = text;
  }

  setSnap(snap: Snap): void {
    if (this.snap === snap) return;
    this.snap = snap;
    this.apply(true);
    this.onSnapChange?.(snap);
  }

  toggle(open: Snap = 'half'): void {
    this.setSnap(this.snap === 'closed' ? open : 'closed');
  }

  private apply(animate: boolean): void {
    const h = window.innerHeight;
    this.root.style.transition = animate ? '' : 'none';
    this.root.style.transform = `translateY(${FRACTION[this.snap] * h}px)`;
    this.root.setAttribute('aria-hidden', this.snap === 'closed' ? 'true' : 'false');
    this.root.classList.toggle('is-open', this.snap !== 'closed');
    this.root.classList.toggle('is-full', this.snap === 'full');
    if (!animate) void this.root.offsetHeight, (this.root.style.transition = '');
  }

  private onDown = (ev: PointerEvent): void => {
    if ((ev.target as HTMLElement).closest('.sheet-close')) return;
    this.height = window.innerHeight;
    this.dragStartY = ev.clientY;
    this.dragFrom = FRACTION[this.snap] * this.height;
    this.header.setPointerCapture(ev.pointerId);
    this.root.style.transition = 'none';
    this.header.addEventListener('pointermove', this.onMove);
    this.header.addEventListener('pointerup', this.onUp);
    this.header.addEventListener('pointercancel', this.onUp);
  };

  private onMove = (ev: PointerEvent): void => {
    if (this.dragFrom === null) return;
    const y = Math.max(
      FRACTION.full * this.height,
      Math.min(this.height, this.dragFrom + (ev.clientY - this.dragStartY)),
    );
    this.root.style.transform = `translateY(${y}px)`;
  };

  private onUp = (ev: PointerEvent): void => {
    if (this.dragFrom === null) return;
    const y = this.dragFrom + (ev.clientY - this.dragStartY);
    this.dragFrom = null;
    this.header.removeEventListener('pointermove', this.onMove);
    this.header.removeEventListener('pointerup', this.onUp);
    this.header.removeEventListener('pointercancel', this.onUp);
    this.root.style.transition = '';

    const fraction = y / this.height;
    const nearest = (['full', 'half', 'closed'] as Snap[]).reduce((best, s) =>
      Math.abs(FRACTION[s] - fraction) < Math.abs(FRACTION[best] - fraction) ? s : best,
    );
    this.snap = nearest;
    this.apply(true);
    this.onSnapChange?.(nearest);
  };
}
